import chordBankData from '@/data/chordBanks.json';
import packMap from '@/data/packMap.json';
import { parseSessionKey } from './musicTheory';

const PITCH_CLASS = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };
const SHARP_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const FLAT_NAMES = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
const FLAT_MAJOR = new Set(['F', 'Bb', 'Eb', 'Ab', 'Db', 'Gb']);
const FLAT_MINOR = new Set(['D', 'G', 'C', 'F', 'Bb', 'Eb']);
const QUALITY_INTERVALS = {
  '': [0, 4, 7], m: [0, 3, 7], maj7: [0, 4, 7, 11], m7: [0, 3, 7, 10], 7: [0, 4, 7, 10],
  m9: [0, 3, 7, 10, 14], maj9: [0, 4, 7, 11, 14], 9: [0, 4, 7, 10, 14], 13: [0, 4, 7, 10, 14, 21],
  6: [0, 4, 7, 9], m6: [0, 3, 7, 9], sus2: [0, 2, 7], sus4: [0, 5, 7], add9: [0, 4, 7, 14],
  'm(add9)': [0, 3, 7, 14], madd9: [0, 3, 7, 14], dim: [0, 3, 6], m7b5: [0, 3, 6, 10],
  '7#9': [0, 4, 7, 10, 15], '7b9': [0, 4, 7, 10, 13], m11: [0, 3, 7, 10, 14, 17], 5: [0, 7],
};

const modulo = (value, base = 12) => ((Number(value) % base) + base) % base;
const canonicalNote = (letter, accidental = '') => `${String(letter).toUpperCase()}${accidental}`;

export function normalizeKey(key) {
  if (key && typeof key === 'object' && typeof key.tonic === 'string') {
    const tonic = canonicalNote(key.tonic[0], key.tonic.slice(1));
    if (PITCH_CLASS[tonic] == null) return null;
    return { tonic, root: PITCH_CLASS[tonic], mode: String(key.mode).toLowerCase().startsWith('maj') ? 'major' : 'minor' };
  }
  const parsed = parseSessionKey(key);
  if (!parsed) return null;
  const match = /^\s*([A-Ga-g])([#b]?)/.exec(String(key));
  return { tonic: canonicalNote(match[1], match[2]), root: parsed.root, mode: parsed.mode };
}

export function parseChord(symbol) {
  const match = /^\s*([A-Ga-g])([#b]?)([^/\s]*)(?:\/([A-Ga-g])([#b]?))?\s*$/.exec(String(symbol || ''));
  if (!match) return null;
  const root = canonicalNote(match[1], match[2]);
  const bass = match[4] ? canonicalNote(match[4], match[5]) : null;
  if (PITCH_CLASS[root] == null || (bass && PITCH_CLASS[bass] == null)) return null;
  const quality = match[3] || '';
  const known = Object.prototype.hasOwnProperty.call(QUALITY_INTERVALS, quality);
  const fallbackMinor = quality.startsWith('m') && !quality.startsWith('maj');
  return {
    symbol: `${root}${quality}${bass ? `/${bass}` : ''}`,
    root,
    rootPitch: PITCH_CLASS[root],
    quality,
    bass,
    bassPitch: bass ? PITCH_CLASS[bass] : null,
    intervals: [...(known ? QUALITY_INTERVALS[quality] : QUALITY_INTERVALS[fallbackMinor ? 'm' : ''])],
    knownQuality: known,
  };
}

const prefersFlats = (keyContext) => {
  const key = normalizeKey(keyContext);
  if (!key) return false;
  return key.mode === 'major' ? FLAT_MAJOR.has(key.tonic) : FLAT_MINOR.has(key.tonic);
};

export function transposeChord(symbol, semitones, keyContext) {
  const chord = parseChord(symbol);
  if (!chord) return null;
  const names = prefersFlats(keyContext) ? FLAT_NAMES : SHARP_NAMES;
  const root = names[modulo(chord.rootPitch + Number(semitones || 0))];
  const bass = chord.bass ? names[modulo(chord.bassPitch + Number(semitones || 0))] : null;
  return `${root}${chord.quality}${bass ? `/${bass}` : ''}`;
}

const ROMANS = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];
const SCALE_OFFSETS = { major: [0, 2, 4, 5, 7, 9, 11], minor: [0, 2, 3, 5, 7, 8, 10] };

function romanSuffix(quality) {
  if (!quality || quality === 'm') return '';
  if (quality === 'dim') return '°';
  if (quality === 'm(add9)') return '(add9)';
  if (quality === 'madd9') return 'add9';
  return quality.startsWith('m') && !quality.startsWith('maj') ? quality.slice(1) : quality;
}

export function romanNumeral(symbol, keyContext) {
  const chord = parseChord(symbol);
  const key = normalizeKey(keyContext);
  if (!chord || !key) return '';
  const relative = modulo(chord.rootPitch - key.root);
  const scale = SCALE_OFFSETS[key.mode];
  let best = { degree: 0, difference: 12 };
  scale.forEach((offset, degree) => {
    for (const shifted of [offset - 12, offset, offset + 12]) {
      const difference = relative - shifted;
      if (Math.abs(difference) < Math.abs(best.difference) || (Math.abs(difference) === Math.abs(best.difference) && difference < best.difference)) best = { degree, difference };
    }
  });
  const minorQuality = (chord.quality.startsWith('m') && !chord.quality.startsWith('maj')) || chord.quality === 'dim';
  const accidental = best.difference < 0 ? 'b'.repeat(Math.abs(best.difference)) : '#'.repeat(best.difference);
  const numeral = minorQuality ? ROMANS[best.degree].toLowerCase() : ROMANS[best.degree];
  return `${accidental}${numeral}${romanSuffix(chord.quality)}`;
}

const candidatesForPitchClass = (pitchClass, low, high) => {
  const result = [];
  for (let pitch = low; pitch <= high; pitch += 1) if (modulo(pitch) === pitchClass) result.push(pitch);
  return result;
};
const nearest = (values, target) => [...values].sort((a, b) => Math.abs(a - target) - Math.abs(b - target) || a - b)[0];

export function voiceProgression(symbols, { low = 48, high = 72, withBass = false } = {}) {
  let center = 60;
  return (symbols || []).map((symbol) => {
    const chord = parseChord(symbol);
    if (!chord) return [];
    const pitchClasses = [...new Set(chord.intervals.map((interval) => modulo(chord.rootPitch + interval)))];
    const voiced = pitchClasses.map((pitchClass) => nearest(candidatesForPitchClass(pitchClass, low, high), center)).filter(Number.isFinite);
    const notes = [...new Set(voiced)].sort((a, b) => a - b);
    if (notes.length) center = notes.reduce((sum, pitch) => sum + pitch, 0) / notes.length;
    if (withBass) notes.unshift(36 + (chord.bassPitch ?? chord.rootPitch));
    return notes;
  });
}

export const CHORD_FAMILIES = chordBankData.families;
export const CHORD_RHYTHMS = chordBankData.rhythms;
export const CHORD_PACKS = packMap.packs;

export function chordOptions({ key, pack = null, family = 'trap' } = {}) {
  const targetKey = normalizeKey(key) || { tonic: 'C', root: 0, mode: 'minor' };
  const options = [];
  if (pack?.progression?.length) {
    const packKey = normalizeKey(pack.key) || targetKey;
    const shift = modulo(targetKey.root - packKey.root);
    const chords = shift ? pack.progression.map((symbol) => transposeChord(symbol, shift, targetKey)) : [...pack.progression];
    options.push({ name: pack.name, chords, roman: chords.map((symbol) => romanNumeral(symbol, targetKey)), source: 'pack' });
  }
  const bank = chordBankData.families[family] || chordBankData.families.trap;
  for (const option of bank?.[targetKey.mode] || []) {
    const chords = option.chords.map((symbol) => transposeChord(symbol, targetKey.root, targetKey));
    options.push({ name: option.name, chords, roman: chords.map((symbol) => romanNumeral(symbol, targetKey)), source: 'bank' });
  }
  return options;
}

export function progressionBarCount(chordCount) {
  const count = Number(chordCount) || 0;
  if (count <= 2) return 2;
  if (count <= 4) return 4;
  return 8;
}

export function chordSchedule(symbols) {
  const chords = Array.isArray(symbols) ? symbols : [];
  const patternBars = progressionBarCount(chords.length);
  return {
    patternBars,
    slots: Array.from({ length: patternBars }, (_, bar) => ({ bar, chordIndex: chords.length === 3 ? Math.min(bar, 2) : bar % Math.max(1, chords.length), symbol: chords[chords.length === 3 ? Math.min(bar, 2) : bar % Math.max(1, chords.length)] })),
  };
}

const rootVoice = (symbol) => {
  const chord = parseChord(symbol);
  return chord ? nearest(candidatesForPitchClass(chord.rootPitch, 48, 72), 60) : 60;
};

export function progressionToNotes(symbols, { rhythm = 'sustain', withBass = false, rootsOnly = false, roman = [] } = {}) {
  const schedule = chordSchedule(symbols);
  const voiced = voiceProgression(symbols, { withBass });
  const rhythmData = chordBankData.rhythms[rhythm] || chordBankData.rhythms.sustain;
  const notes = [];
  const labels = schedule.slots.map((slot) => ({ bar: slot.bar, symbol: slot.symbol, roman: roman[slot.chordIndex] || '' }));
  const add = (slot, start, length, pitch, index) => notes.push({ id: `chord-${slot.bar}-${index}-${notes.length}`, start, length, pitch, velocity: pitch < 48 ? 0.9 : 0.8 });
  const pitchesFor = (slot) => rootsOnly ? [rootVoice(slot.symbol)] : (voiced[slot.chordIndex] || []);

  if (rhythm === 'push') {
    schedule.slots.forEach((slot, slotIndex) => {
      const start = slotIndex === 0 ? 0 : slot.bar * 4 - 0.5;
      const end = slotIndex + 1 < schedule.slots.length ? (slot.bar + 1) * 4 - 0.5 : schedule.patternBars * 4;
      pitchesFor(slot).forEach((pitch, index) => add(slot, start, end - start, pitch, index));
    });
  } else if (rhythm === 'arp') {
    schedule.slots.forEach((slot) => {
      const pitches = pitchesFor(slot).filter((pitch) => pitch >= 48).sort((a, b) => a - b);
      for (let hit = 0; hit < 8; hit += 1) add(slot, slot.bar * 4 + hit * 0.5, 0.5, pitches[hit % Math.max(1, pitches.length)] ?? rootVoice(slot.symbol), hit);
      if (withBass && !rootsOnly) (voiced[slot.chordIndex] || []).filter((pitch) => pitch < 48).forEach((pitch, index) => add(slot, slot.bar * 4, 4, pitch, `bass-${index}`));
    });
  } else if (rhythmData.hits16 || rhythmData.hits12) {
    const grid = rhythmData.hits12 ? 12 : 16;
    const hits = rhythmData.hits12 || rhythmData.hits16;
    const length = (rhythmData.len12 || rhythmData.len16 || 1) * 4 / grid;
    schedule.slots.forEach((slot) => hits.forEach((hit) => pitchesFor(slot).forEach((pitch, index) => add(slot, slot.bar * 4 + (hit - 1) * 4 / grid, length, pitch, `${hit}-${index}`))));
  } else {
    schedule.slots.forEach((slot, slotIndex) => {
      if (slotIndex > 0 && slot.chordIndex === schedule.slots[slotIndex - 1].chordIndex) return;
      let bars = 1;
      while (slotIndex + bars < schedule.slots.length && schedule.slots[slotIndex + bars].chordIndex === slot.chordIndex) bars += 1;
      pitchesFor(slot).forEach((pitch, index) => add(slot, slot.bar * 4, bars * 4, pitch, index));
    });
  }
  return { notes, patternBars: schedule.patternBars, chords: labels };
}

export const __chordDataVersion = chordBankData.version;
